import React, { useEffect, useState } from 'react';
import { History } from 'lucide-react';

// Últimos cambios de horarios: quién (la profesional o su secretaria) cambió
// la semana, cerró o abrió un día, o bloqueó, y cuándo (oct-2026). Sale de
// GET /api/schedule/professional/:id/cambios; el registro lo arma el backend
// en cada cambio (utils/registroHorarios).

type Cambio = { id: number; actor_nombre: string | null; actor_rol: string | null; detalle: string; created_at: string };

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const A_LA_VISTA = 5;

const cuando = (iso: string) => new Date(iso).toLocaleString('es-AR', {
  weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
});

const HistorialHorarios: React.FC<{ professionalId: number | string }> = ({ professionalId }) => {
  const [cambios, setCambios] = useState<Cambio[] | null>(null);
  const [todos, setTodos] = useState(false);

  useEffect(() => {
    let cancelado = false;
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/api/schedule/professional/${professionalId}/cambios?limit=50`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelado) setCambios(d?.success ? d.cambios : []); })
      .catch(() => { if (!cancelado) setCambios([]); });
    return () => { cancelado = true; };
  }, [professionalId]);

  if (cambios === null) return null;
  const visibles = todos ? cambios : cambios.slice(0, A_LA_VISTA);

  return (
    <section className="bg-white rounded-3xl border border-gray-200 shadow-sm p-4 sm:p-5 text-left">
      <div className="flex items-center gap-2.5 mb-3">
        <span className="w-8 h-8 rounded-xl flex items-center justify-center bg-slate-100">
          <History className="w-4 h-4 text-slate-700" />
        </span>
        <h3 className="text-base font-bold text-gray-900">Últimos cambios</h3>
      </div>
      {cambios.length === 0 ? (
        <p className="text-sm text-gray-500">
          Todavía no hay cambios anotados. Desde ahora queda registrado quién cambia la semana, los días cerrados, los días especiales y los bloqueos.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {visibles.map((c) => (
            <li key={c.id} className="py-2">
              <p className="text-sm text-gray-800">{c.detalle}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {cuando(c.created_at)} · {c.actor_nombre || 'Alguien'}
                {c.actor_rol === 'secretaria' && <span className="ml-1 rounded-md bg-indigo-50 text-indigo-700 px-1.5 py-0.5 text-[11px] font-medium">secretaria</span>}
              </p>
            </li>
          ))}
        </ul>
      )}
      {cambios.length > A_LA_VISTA && (
        <button
          type="button"
          onClick={() => setTodos(!todos)}
          className="mt-2 rounded-xl border border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
          aria-expanded={todos}
        >
          {todos ? 'Ver menos' : `Ver ${cambios.length - A_LA_VISTA} más`}
        </button>
      )}
    </section>
  );
};

export default HistorialHorarios;
